import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Page from 'flarum/common/components/Page';
import NotificationsDropdown from 'flarum/forum/components/NotificationsDropdown';

// ===== iOS app icon badge =====
// iOS 16.4+ shows the red number badge on Home Screen web apps only through the
// Badging API. fof/pwa never calls it, so the badge stayed empty. Here we mirror
// the bell's unread count on the icon while the app is open, and keep that count
// in IndexedDB so the service worker can bump it when a push arrives while the
// app is closed (see Forum\Controller\ServiceWorkerController).

const DB_NAME = 'keyval-store';
const STORE = 'keyval';
const COUNT_KEY = 'florcarioca.unreadCount';

const readCount = () => (app.session.user ? app.session.user.unreadNotificationCount() || 0 : 0);

// Persist the count for the service worker. Best-effort: a failure here must not
// affect the page.
const persistCount = (count) => {
  try {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };

    request.onsuccess = () => {
      try {
        request.result.transaction(STORE, 'readwrite').objectStore(STORE).put(count, COUNT_KEY);
      } catch (e) {
        // ignore storage errors
      }
    };
  } catch (e) {
    // ignore storage errors (private browsing, etc.)
  }
};

const applyBadge = (count) => {
  count = count > 0 ? count : 0;
  persistCount(count);

  if (!('setAppBadge' in navigator)) {
    return;
  }

  try {
    if (count > 0) {
      navigator.setAppBadge(count).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  } catch (e) {
    // A badge is a nicety: never let it break the header.
  }
};

export default function initAppBadge() {
  let shown = null;

  const sync = (force = false) => {
    const count = readCount();

    // A push may have changed the badge while the app was in the background, so
    // the bell's count is re-applied (force) when the app comes back to front.
    if (!force && count === shown) {
      return;
    }

    shown = count;
    applyBadge(count);
  };

  // The bell is redrawn whenever the unread count changes (reading a
  // notification, "Mark All as Read", a new notification arriving).
  extend(NotificationsDropdown.prototype, 'oncreate', () => sync());
  extend(NotificationsDropdown.prototype, 'onupdate', () => sync());

  // Runs once per page, and again when returning to the app.
  extend(Page.prototype, 'oncreate', () => sync(true));

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      shown = null;
      sync();
    }
  });
}
